<template>
    <div class="table-of-contents">
        <LoadingIndicator v-if="localItems.length === 0" />
        <template v-else>
            <h3 v-if="title" class="table-of-contents__title">{{ title }}</h3>
            <div
                class="table-of-contents__items"
                :class="modifierClasses"
                :style="`--collapsed-height: ${collapsedHeight}`"
            >
                <div v-for="(item, i) in localItems" :key="`toc-${i}`">
                    <div class="table-of-contents__item">
                        <div class="table-of-contents__item__title">
                            <a :href="`#${item.id}`">{{ item.title }}</a>
                        </div>
                        <div v-if="item.subHeadings" class="table-of-contents__item__subheadings">
                            <div
                                v-for="subHeading in item.subHeadings"
                                :key="subHeading.id"
                                class="table-of-contents__item__subheading"
                            >
                                <a :href="`#${subHeading.id}`">{{ subHeading.title }}</a>
                            </div>
                        </div>
                    </div>
                    <div v-if="item !== localItems[localItems.length - 1]" class="table-of-contents__separator" />
                </div>
            </div>
            <div v-if="showCollapser" class="table-of-contents__collapser">
                <div class="table-of-contents__collapser__line" />
                <svg-icon
                    :name="collapsed ? 'double_arrow_circle_down' : 'double_arrow_circle_up'"
                    @click="collapsed = !collapsed"
                />
            </div>
        </template>
    </div>
</template>

<script>
import LoadingIndicator from '@/components/LoadingIndicator.vue';

const MAX_COLLAPSED_ITEMS = 5;

export default {
    name: 'TableOfContents',
    components: {
        LoadingIndicator,
    },
    props: {
        items: {
            type: Array,
            default: () => [],
        },
        showH3: {
            type: Boolean,
            default: true,
        },
        limitClass: {
            type: String,
            default: '',
        },
        excludedIds: {
            type: Array,
            default: () => [],
        },
        title: {
            type: String,
            default: null,
        },
    },
    data() {
        return {
            collapsed: true,
            localItems: this.items,
            collapsedHeight: 'auto',
            isFullToCReady: false,
        };
    },
    computed: {
        modifierClasses() {
            return {
                'table-of-contents__items--collapsed': this.collapsed,
            };
        },
        totalHeadingsAndSubheadings() {
            return this.localItems.reduce((count, item) => {
                // Add one for the main heading
                count += 1;

                // Add the count of subheadings for this item
                count += item.subHeadings ? item.subHeadings.length : 0;

                return count;
            }, 0);
        },
        showCollapser() {
            return this.totalHeadingsAndSubheadings > MAX_COLLAPSED_ITEMS && this.collapsedHeight !== 'auto';
        },
    },
    mounted() {
        this.generateInitialTableOfContents(); // Generate and display initial partial ToC

        this.$nextTick(() => {
            const container = this.$el.querySelector('.table-of-contents__items');

            if (container) {
                container.classList.add('table-of-contents__items--transparent');

                const height = container.offsetHeight;
                this.collapsedHeight = `${height}px`;

                container.classList.remove('table-of-contents__items--transparent');

                this.prepareFullTableOfContentsAsync();
            }
        });
    },

    methods: {
        generateInitialTableOfContents() {
            // If no items were sent we get them from the DOM
            let initialHeadings;

            if (this.limitClass) {
                const parentNode = document.querySelector(`.${this.limitClass}`);

                initialHeadings = Array.from(parentNode.querySelectorAll('h2, h3')).slice(0, MAX_COLLAPSED_ITEMS);
            } else {
                initialHeadings = Array.from(document.querySelectorAll('h2, h3')).slice(0, MAX_COLLAPSED_ITEMS);
            }

            this.localItems = this.processHeadings(initialHeadings);
        },
        async prepareFullTableOfContentsAsync() {
            await this.$nextTick();
            let remainingHeadings;

            if (this.limitClass) {
                const parentNode = document.querySelector(`.${this.limitClass}`);
                remainingHeadings = Array.from(parentNode.querySelectorAll('h2, h3')).slice(MAX_COLLAPSED_ITEMS);
            } else {
                remainingHeadings = Array.from(document.querySelectorAll('h2, h3')).slice(MAX_COLLAPSED_ITEMS);
            }

            this.localItems = [...this.localItems, ...this.processHeadings(remainingHeadings)];

            if (this.totalHeadingsAndSubheadings <= MAX_COLLAPSED_ITEMS) {
                this.collapsed = false;
            }

            this.isFullToCReady = true;
        },
        processHeadings(headings) {
            const items = [];
            let currentItem = null;

            headings.forEach(heading => {
                if (this.excludedIds.includes(heading.id)) {
                    return;
                }

                if (!heading.id) {
                    heading.id = heading.innerText.replace(/\s/g, '-').toLowerCase();
                }

                if (heading.tagName.toLowerCase() === 'h2') {
                    if (currentItem) {
                        items.push(currentItem);
                    }

                    currentItem = {
                        id: heading.id,
                        title: heading.innerText,
                        subHeadings: [],
                    };
                } else if (heading.tagName.toLowerCase() === 'h3' && currentItem && this.showH3) {
                    currentItem.subHeadings.push({
                        id: heading.id,
                        title: heading.innerText,
                    });
                }
            });

            if (currentItem) {
                items.push(currentItem);
            }

            return items;
        },
    },
};
</script>

<style lang="scss">
html {
    scroll-behavior: smooth;
}
</style>

<style scoped lang="scss">
@import '@/sass/_variables.scss';

.table-of-contents {
    width: -webkit-fill-available;
    height: fit-content;
    color: $primary;
    text-align: left;

    @include md {
        width: fit-content;
    }

    &__title {
        @extend %heading-3;
        margin-bottom: $sm-magazine-spacing;
    }

    &__items {
        transition: max-height 0.5s ease-in;
        max-height: 1000px;
        overflow: hidden;

        &--collapsed {
            transition: max-height 0.5s ease-out;
            max-height: var(--collapsed-height);
        }

        &--transparent {
            opacity: 0;
        }
    }

    &__item {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        width: 100%;
        height: fit-content;
        padding: 8px 0;
        margin-right: 16px;

        &__title {
            width: 100%;
            height: fit-content;

            a {
                color: $primary;
                text-decoration: none;
                font-weight: bold;
            }
        }

        &__subheadings {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            justify-content: center;
            width: 100%;
            height: fit-content;
            margin-left: 16px;
            @include font-size(14px);

            & > *:first-child {
                margin-top: 8px;
            }

            & > *:last-child {
                margin-bottom: 0;
            }
        }

        &__subheading {
            width: 100%;
            height: fit-content;
            margin-bottom: 8px;

            a {
                color: $primary;
                text-decoration: none;
                font-weight: bold;
            }
        }

        &__title,
        &__subheading {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
        }
    }

    &__separator {
        width: 100%;
        height: 1px;
        margin-right: 16px;
        background: $primary;
    }

    &__collapser {
        $icon-size: 24px;

        position: relative;
        width: 100%;
        height: fit-content;
        margin-top: 16px + calc($icon-size / 2);
        margin-bottom: 10px; // to expand to bottom of the icon

        &__line {
            width: 100%;
            height: 3px;
            background: $primary;
        }

        .svg-icon {
            position: absolute;
            top: 0;
            left: 50%;
            transform: translate(-50%, -50%);
            width: $icon-size;
            height: $icon-size;
            cursor: pointer;
        }
    }
}
</style>
